import React from "react";
import { array } from "fp-ts";
import { pipe } from "fp-ts/lib/function";
import { TContainerStateProps } from "../state/TContainerStateProps";
import { CRMPageWrap } from "../components/Simple/CRMPageWrap/CRMPageWrap";
import { CRMPageBody } from "../components/Simple/CRMPageBody/CRMPageBody";
import { CRMHomesMenuBarContainer } from "../containers/CRMHomesMenuBarContainer/CRMHomesMenuBarContainer";
import { CRMCardOutside } from "../components/CRMCardOutside/CRMCardOutside";
import { CRMPadding } from "../components/Simple/CRMPadding/CRMPadding";
import { CRMTitleSection } from "../components/CRMTitleSection/CRMTitleSection";
import { CRMSpacingColumn } from "../components/CRMSpacingColumn/CRMSpacingColumn";
import { SpacingColumn } from "../components/BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../components/BuildingBlocks/SpacingRow";
import { Padding } from "../components/BuildingBlocks/Padding";
import { CRMSpacing } from "../models/CRMSpacing";
import { CRMColors } from "../models/CRMColors";
import { CRMStatusPill } from "../components/Simple/CRMStatusPill/CRMStatusPill";
import { CRMLink } from "../components/CRMLink/CRMLink";
import { CRMParagraph } from "../components/Simple/CRMParagraph/CRMParagraph";
import { CRMCodecEditForm } from "../components/CRMCodecEditForm/CRMCodecEditForm";
import { CRMAutosaveIndicatorWrap } from "../components/CRM/CRMAutosaveIndicatorWrap/CRMAutosaveIndicatorWrap";
import { CRMFormButton } from "../components/CRMFormButton/CRMFormButton";
import { CRMButtonPrimary } from "../components/CRMButtonPrimary/CRMButtonPrimary";
import { CRMPartyMembersFormContainer } from "../containers/CRMPartyMembersFormContainer/CRMPartyMembersFormContainer";
import { PartySituationFields, PartySearchCriteriaFields, PartyNotesFields } from "../../../domain/codecs/Party";
import { AddPartyListingFields } from "../../../domain/codecs/form/AddPartyListingForm";
import { FormStatus_highestPriority } from "../../../shared/src/codecs/codec";
import { snakeCaseToCopyText } from "../../../shared/src/util";
import { getUrlStringForRoute } from "../state/router/getUrlStringsForRoute";
import { paths } from "../state/router/routerPaths";

const CRMPartyPage = (props: TContainerStateProps): JSX.Element => {
    const output = props.state.forms.party_page.data.output;
    const form = output.form;

    const partyName = pipe(
        output.members,
        array.map((m) => `${m.edited.first_name} ${m.edited.last_name}`.trim()),
        array.filter((name) => name !== ""),
        (names) => names.length > 0 ? names.join(" & ") : "Party",
    );

    const roleLabel =
        output.read_only.is_seller_party && output.read_only.is_buyer_party ? "Seller & buyer party"
            : output.read_only.is_seller_party ? "Seller party"
                : output.read_only.is_buyer_party ? "Buyer party"
                    : "Party";

    const detailStatus = form.status;

    const membersStatus = FormStatus_highestPriority(output.members.map((m) => m.status));

    return (
        <CRMPageWrap>
            <CRMHomesMenuBarContainer {...props} />

            <CRMPageBody>
                <Padding type="vertical" spacing={CRMSpacing.LARGE}>
                    <SpacingColumn spacing={CRMSpacing.LARGE}>

                        {/* HEADER */}
                        <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="center">
                            <CRMTitleSection>{partyName}</CRMTitleSection>
                            <CRMStatusPill
                                pillColor={CRMColors.HIGHLIGHTS_CALMING_PINK_12}
                                borderColor={CRMColors.HIGHLIGHTS_CALMING_PINK_8}
                                textColor={CRMColors.PRIMARY_2}
                                label={roleLabel}
                            />
                        </SpacingRow>

                        <SpacingRow spacing={CRMSpacing.X_LARGE} childSize="2fr 1fr" alignItems="flex-start">

                            {/* LEFT COLUMN */}
                            <SpacingColumn spacing={CRMSpacing.LARGE}>

                                {/* PEOPLE */}
                                <CRMCardOutside borderRounding="right" shadow={true}>
                                    <CRMPadding size="large">
                                        <CRMAutosaveIndicatorWrap status={membersStatus}>
                                            <CRMSpacingColumn spacing="large">
                                                <CRMTitleSection>People</CRMTitleSection>
                                                <CRMPartyMembersFormContainer state={props.state} dispatch={props.dispatch} />
                                            </CRMSpacingColumn>
                                        </CRMAutosaveIndicatorWrap>
                                    </CRMPadding>
                                </CRMCardOutside>

                                {/* SITUATION */}
                                <CRMCardOutside borderRounding="right" shadow={true}>
                                    <CRMPadding size="large">
                                        <CRMAutosaveIndicatorWrap status={detailStatus}>
                                            <CRMSpacingColumn spacing="large">
                                                <CRMTitleSection>Situation</CRMTitleSection>
                                                <CRMCodecEditForm
                                                    codec={PartySituationFields}
                                                    model={form.edited}
                                                    validationErrors={form.validationErrors}
                                                    onChange={(edited) => props.dispatch({
                                                        type: "PARTY_PAGE_FORM_CHANGE",
                                                        payload: { ...form, edited: { ...form.edited, ...edited } },
                                                    })}
                                                />
                                            </CRMSpacingColumn>
                                        </CRMAutosaveIndicatorWrap>
                                    </CRMPadding>
                                </CRMCardOutside>

                                {/* SEARCH CRITERIA */}
                                <CRMCardOutside borderRounding="right" shadow={true}>
                                    <CRMPadding size="large">
                                        <CRMAutosaveIndicatorWrap status={detailStatus}>
                                            <CRMSpacingColumn spacing="large">
                                                <CRMTitleSection>Search criteria</CRMTitleSection>
                                                <CRMCodecEditForm
                                                    codec={PartySearchCriteriaFields}
                                                    model={form.edited}
                                                    validationErrors={form.validationErrors}
                                                    onChange={(edited) => props.dispatch({
                                                        type: "PARTY_PAGE_FORM_CHANGE",
                                                        payload: { ...form, edited: { ...form.edited, ...edited } },
                                                    })}
                                                />
                                            </CRMSpacingColumn>
                                        </CRMAutosaveIndicatorWrap>
                                    </CRMPadding>
                                </CRMCardOutside>

                            </SpacingColumn>

                            {/* RIGHT COLUMN */}
                            <SpacingColumn spacing={CRMSpacing.LARGE}>

                                {/* NOTES */}
                                <CRMCardOutside borderRounding="right" shadow={true}>
                                    <CRMPadding size="large">
                                        <CRMAutosaveIndicatorWrap status={detailStatus}>
                                            <CRMSpacingColumn spacing="large">
                                                <CRMTitleSection>Notes</CRMTitleSection>
                                                <CRMCodecEditForm
                                                    codec={PartyNotesFields}
                                                    model={form.edited}
                                                    validationErrors={form.validationErrors}
                                                    onChange={(edited) => props.dispatch({
                                                        type: "PARTY_PAGE_FORM_CHANGE",
                                                        payload: { ...form, edited: { ...form.edited, ...edited } },
                                                    })}
                                                />
                                            </CRMSpacingColumn>
                                        </CRMAutosaveIndicatorWrap>
                                    </CRMPadding>
                                </CRMCardOutside>

                                {/* SELLING */}
                                <CRMCardOutside borderRounding="right" shadow={true}>
                                    <CRMPadding size="large">
                                        <CRMSpacingColumn spacing="large">
                                            <CRMTitleSection>{`Selling (${output.read_only.listings.length})`}</CRMTitleSection>
                                            {output.read_only.listings.length === 0 &&
                                                <CRMParagraph>This party is not selling any properties.</CRMParagraph>
                                            }
                                            {pipe(
                                                output.read_only.listings,
                                                array.mapWithIndex((i, listing) =>
                                                    <SpacingRow key={i} spacing={CRMSpacing.MEDIUM} justifyContent="space-between">
                                                        <CRMLink
                                                            href={getUrlStringForRoute(paths, "VIEW_CRM_LISTING", { listingId: listing.id }, {})}
                                                            linkStyle="normal"
                                                        >
                                                            {listing.address_short_string || "Address unknown"}
                                                        </CRMLink>
                                                        <CRMStatusPill
                                                            pillColor={CRMColors.PRIMARY_16}
                                                            borderColor={CRMColors.PRIMARY_12}
                                                            textColor={CRMColors.PRIMARY_4}
                                                            label={snakeCaseToCopyText(listing.status)}
                                                        />
                                                    </SpacingRow>
                                                ),
                                            )}
                                        </CRMSpacingColumn>
                                    </CRMPadding>
                                </CRMCardOutside>

                                {/* ENQUIRIES (properties this party has enquired about as a buyer) */}
                                <CRMCardOutside borderRounding="right" shadow={true}>
                                    <CRMPadding size="large">
                                        <CRMSpacingColumn spacing="large">
                                            <CRMTitleSection>{`Enquiries (${output.read_only.enquiries.length})`}</CRMTitleSection>
                                            {output.read_only.enquiries.length === 0 &&
                                                <CRMParagraph>This party has not enquired about any properties.</CRMParagraph>
                                            }
                                            {pipe(
                                                output.read_only.enquiries,
                                                array.mapWithIndex((i, enquiry) =>
                                                    <SpacingRow key={i} spacing={CRMSpacing.MEDIUM} justifyContent="space-between">
                                                        <CRMLink
                                                            href={getUrlStringForRoute(paths, "VIEW_CRM_ENQUIRY", { enquiryId: enquiry.enquiry_id }, {})}
                                                            linkStyle="normal"
                                                        >
                                                            {enquiry.address_short_string || "Address unknown"}
                                                        </CRMLink>
                                                        <CRMStatusPill
                                                            pillColor={CRMColors.PRIMARY_16}
                                                            borderColor={CRMColors.PRIMARY_12}
                                                            textColor={CRMColors.PRIMARY_4}
                                                            label={snakeCaseToCopyText(enquiry.status)}
                                                        />
                                                    </SpacingRow>
                                                ),
                                            )}
                                        </CRMSpacingColumn>
                                    </CRMPadding>
                                </CRMCardOutside>

                                {/* ADD PROPERTY */}
                                <CRMCardOutside borderRounding="right" shadow={true}>
                                    <CRMPadding size="large">
                                        <CRMSpacingColumn spacing="large">
                                            <CRMTitleSection>Add property</CRMTitleSection>
                                            <CRMParagraph>
                                                Creates a listing for this seller party and starts the Sail Valuations
                                                discovery case, so enter the address before creating.
                                            </CRMParagraph>
                                            <CRMCodecEditForm
                                                codec={AddPartyListingFields}
                                                model={output.add_listing.edited}
                                                validationErrors={output.add_listing.validationErrors}
                                                onChange={(edited) => props.dispatch({
                                                    type: "PARTY_PAGE_ADD_LISTING_FORM_CHANGE",
                                                    payload: { ...output.add_listing, edited: { ...output.add_listing.edited, ...edited } },
                                                })}
                                            />
                                            <CRMFormButton
                                                formStatus={output.add_listing.status}
                                                ButtonElement={(buttonProps) => <CRMButtonPrimary {...buttonProps} />}
                                                label="Create listing"
                                                fullWidth={true}
                                                enabledOnUntouched={true}
                                                onClick={() => props.dispatch({ type: "PARTY_PAGE_ADD_LISTING_SUBMIT" })}
                                            />
                                        </CRMSpacingColumn>
                                    </CRMPadding>
                                </CRMCardOutside>

                            </SpacingColumn>

                        </SpacingRow>
                    </SpacingColumn>
                </Padding>
            </CRMPageBody>
        </CRMPageWrap>
    );
};

export default CRMPartyPage;
